import { CpdStudio } from "@/components/admin/CpdStudio";
import { TeachingVideo } from "@/components/TeachingTileCard";
import { allowedVideoEmbedUrl } from "@/lib/learn";
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminNav } from "@/components/admin/AdminNav";
import { api } from "@/lib/api";
import type { DirectoryResponse, Facility } from "@/lib/public-directory";
import { SEO } from "@/lib/seo";
import "@/styles/brand-a.css";
import "@/styles/admin-invites.css";
import "@/styles/phase3-loops.css";

type TileStatus = "draft" | "published";

interface AdminTile {
  id: string;
  facilityId: string;
  facilityName: string;
  title: string;
  body: string;
  videoUrl: string | null;
  specialtySlug: string;
  status: TileStatus;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AdminTilesResponse {
  items: AdminTile[];
}

export default function AdminTiles() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<TileStatus | "">("");
  const [facilitySearch, setFacilitySearch] = useState("");
  const [facility, setFacility] = useState<Facility | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [videoUrl, setVideoUrl] = useState("");

  const tiles = useQuery({
    queryKey: ["admin-tiles", status],
    queryFn: () =>
      api.get<AdminTilesResponse>("/v1/admin/tiles", {
        params: { status: status || undefined },
      }),
    retry: false,
  });
  const facilities = useQuery({
    queryKey: ["admin-tile-facility-search", facilitySearch.trim()],
    queryFn: () =>
      api.get<DirectoryResponse>("/v1/public/canberra/directory", {
        skipAuth: true,
        params: { type: "specialist", q: facilitySearch.trim(), limit: 12 },
      }),
    enabled: facilitySearch.trim().length >= 2 && !facility,
    retry: false,
  });
  const createTile = useMutation({
    mutationFn: () =>
      api.post<AdminTile>("/v1/admin/tiles", {
        facilityId: facility?.id ?? "",
        title: title.trim(),
        body: body.trim(),
        videoUrl: videoUrl.trim() || null,
      }),
    onSuccess: () => {
      setFacility(null);
      setFacilitySearch("");
      setTitle("");
      setBody("");
      setVideoUrl("");
      void queryClient.invalidateQueries({ queryKey: ["admin-tiles"] });
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    createTile.mutate();
  };

  return (
    <div className="brand-a pb-admin-invites-page pb-admin-tiles-page">
      <SEO
        title="CPD studio · PulseBoard"
        description="Prepare and publish specialist teaching sessions."
        path="/admin/tiles"
        noindex
      />
      <main className="pb-admin-invites-main">
        <AdminNav />
        <header className="pb-admin-heading">
          <div>
            <p>Admin · Five Minute CPD</p>
            <h1>CPD studio</h1>
          </div>
          <button type="button" onClick={() => void tiles.refetch()}>
            Refresh
          </button>
        </header>

        <CpdStudio
          onImport={(pkg, notes) => {
            setTitle(pkg.title);
            setBody(notes);
            setVideoUrl(pkg.videoUrl);
            setFacility(null);
            setFacilitySearch(pkg.specialistName);
          }}
        />
        <section
          className="pb-admin-tile-create"
          aria-labelledby="create-tile-heading"
        >
          <div className="pb-admin-section-heading">
            <div>
              <h2 id="create-tile-heading">Create a draft</h2>
              <p>Link one source-checked specialist before drafting.</p>
            </div>
          </div>
          <form onSubmit={submit}>
            <label className="pb-admin-tile-facility">
              Specialist
              <input
                value={facility ? facility.name : facilitySearch}
                onChange={(event) => {
                  setFacility(null);
                  setFacilitySearch(event.target.value);
                }}
                placeholder="Search specialist or practice"
                required
              />
              {!facility && facilities.data?.items.length ? (
                <ul className="pb-admin-facility-results">
                  {facilities.data.items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setFacility(item);
                          setFacilitySearch(item.name);
                        }}
                      >
                        <strong>{item.name}</strong>
                        <span>
                          {item.address_json.practice_name ??
                            item.address_json.specialty ??
                            "Specialist"}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {facilities.isError ? (
                <span className="pb-loop-error">
                  Facility search is unavailable.
                </span>
              ) : null}
            </label>
            <label>
              Title
              <input
                required
                maxLength={90}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <label>
              Session notes, sources and disclosures
              <textarea
                required
                rows={8}
                maxLength={1500}
                value={body}
                onChange={(event) => setBody(event.target.value)}
              />
            </label>
            <label>
              Video URL
              <input
                type="url"
                value={videoUrl}
                onChange={(event) => setVideoUrl(event.target.value)}
                placeholder="Optional YouTube or Vimeo URL"
              />
            </label>
            <button
              type="submit"
              disabled={
                createTile.isPending ||
                !facility ||
                !title.trim() ||
                !body.trim()
              }
            >
              {createTile.isPending ? "Creating…" : "Create draft"}
            </button>
            {createTile.isError ? (
              <p className="pb-loop-error" role="alert">
                The draft could not be created.
              </p>
            ) : null}
            {createTile.isSuccess ? (
              <p className="pb-loop-success" role="status">
                Draft created.
              </p>
            ) : null}
          </form>
        </section>

        <section
          className="pb-admin-tile-list"
          aria-labelledby="tile-list-heading"
        >
          <div className="pb-admin-section-heading pb-admin-section-heading--tabs">
            <div>
              <h2 id="tile-list-heading">Sessions</h2>
              <p>Edit, publish, or remove drafts.</p>
            </div>
            <label>
              Status
              <select
                value={status}
                onChange={(event) =>
                  setStatus(event.target.value as TileStatus | "")
                }
              >
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </label>
          </div>
          {tiles.isLoading ? (
            <AdminTileState>Loading tiles…</AdminTileState>
          ) : null}
          {tiles.isError ? (
            <AdminTileState error>Tiles could not be loaded.</AdminTileState>
          ) : null}
          {tiles.data?.items.length === 0 ? (
            <AdminTileState>No tiles match this status.</AdminTileState>
          ) : null}
          <div className="pb-admin-tile-cards">
            {tiles.data?.items.map((tile) => (
              <AdminTileEditor key={tile.id} tile={tile} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function AdminTileEditor({ tile }: { tile: AdminTile }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(tile.title);
  const [body, setBody] = useState(tile.body);
  const [videoUrl, setVideoUrl] = useState(tile.videoUrl ?? "");
  const update = useMutation({
    mutationFn: (payload: {
      title?: string;
      body?: string;
      videoUrl?: string | null;
      status?: TileStatus;
    }) => api.patch<AdminTile>(`/v1/admin/tiles/${tile.id}`, payload),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["admin-tiles"] }),
  });
  const dirty =
    title.trim() !== tile.title ||
    body.trim() !== tile.body ||
    (videoUrl.trim() || null) !== tile.videoUrl;
  const embedUrl = allowedVideoEmbedUrl(videoUrl);
  const remove = useMutation({
    mutationFn: () => api.delete<void>(`/v1/admin/tiles/${tile.id}`),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["admin-tiles"] }),
  });

  return (
    <article className="pb-admin-tile-editor">
      <header>
        <div>
          <span className="pb-admin-status" data-status={tile.status}>
            {tile.status}
          </span>
          <h3>{tile.facilityName}</h3>
        </div>
        <small>{tile.specialtySlug}</small>
      </header>
      <label>
        Title
        <input
          maxLength={90}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>
      <label>
        Session notes, sources and disclosures
        <textarea
          rows={7}
          maxLength={1500}
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
      </label>
      <label>
        Video URL
        <input
          type="url"
          value={videoUrl}
          onChange={(event) => setVideoUrl(event.target.value)}
        />
      </label>
      <p className="pb-material-help">Attach reviewed materials in the notes using [Slides: Title](https://...) or [Handout: Title](https://...). PDFs hosted under /teaching-materials/ on PulseBoard display beside the video. External files open at their original host.</p>
      {embedUrl ? <TeachingVideo embedUrl={embedUrl} title={title} /> : null}
      {dirty ? (
        <p>
          Save your changes before publishing. Review the final video, captions,
          sources, disclosures and speaker permission.
        </p>
      ) : null}
      <div className="pb-admin-tile-actions">
        <button
          type="button"
          disabled={update.isPending || !title.trim() || !body.trim()}
          onClick={() =>
            update.mutate({
              title: title.trim(),
              body: body.trim(),
              videoUrl: videoUrl.trim() || null,
            })
          }
        >
          Save changes
        </button>
        {tile.status === "draft" ? (
          <button
            type="button"
            disabled={update.isPending || dirty}
            onClick={() => update.mutate({ status: "published" })}
          >
            Publish
          </button>
        ) : null}
        {tile.status === "published" ? (
          <button
            type="button"
            disabled={update.isPending}
            onClick={() => update.mutate({ status: "draft" })}
          >
            Withdraw to draft
          </button>
        ) : null}
        {tile.status === "draft" ? (
          <button
            type="button"
            className="pb-admin-danger"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Delete draft
          </button>
        ) : null}
      </div>
      {update.isSuccess ? (
        <p className="pb-loop-success" role="status">
          Tile updated.
        </p>
      ) : null}
      {update.isError || remove.isError ? (
        <p className="pb-loop-error" role="alert">
          The tile could not be updated.
        </p>
      ) : null}
    </article>
  );
}

function AdminTileState({
  children,
  error = false,
}: {
  children: React.ReactNode;
  error?: boolean;
}) {
  return (
    <div
      className={`pb-admin-state${error ? " pb-admin-state--error" : ""}`}
      role={error ? "alert" : undefined}
    >
      {children}
    </div>
  );
}
